import { getCollectionDocuments } from '@/actions/collection';
import { EXPORT_METHODS, ExportMethod } from '@/components/DataIngestion/_constants/data-ingestion';
import { DataIngestion } from '@/payload-types';
import { Button, FieldError, LoadingOverlay, RelationshipInput, toast, useListQuery } from '@payloadcms/ui';
import { useState } from 'react';

import GoogleSheetImportFromExport from './components/GoogleSheetImportFromExport/GoogleSheetImportFromExport';

const FROM_EXPORT_IMPORT_COMPONENTS_BY_EXPORT_METHOD: Record<ExportMethod, React.ComponentType<FromExportImportComponentProps>> = {
  [EXPORT_METHODS.GOOGLE_SHEET]: GoogleSheetImportFromExport,
};

export interface FromExportImportComponentProps {
  exportDocument: DataIngestion;
}

const ImportFromExportedRecord = () => {
  const { collectionSlug } = useListQuery();
  const [exportDocumentId, setExportDocumentId] = useState<string | null>(null);
  const [exportDocument, setExportDocument] = useState<DataIngestion | null>(null);
  const [exportDocumentError, setExportDocumentError] = useState<boolean>(false);
  const [derivedImportMethod, setDerivedImportMethod] = useState<ExportMethod | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!exportDocumentId) {
      setExportDocumentError(true);
      return;
    }

    setExportDocumentError(false);
    setIsLoading(true);

    try {
      const { docs: exportDocuments } = await getCollectionDocuments({
        collectionSlug: 'data-ingestion',
        options: { where: { id: { equals: exportDocumentId } } },
      });

      const exportDocument = exportDocuments[0] as DataIngestion;
      if (!exportDocument) {
        toast.error('Export document not found');
        return;
      }
      setExportDocument(exportDocument);

      const method = exportDocument.export?.method?.[0]?.blockType;
      if (!method) {
        toast.error('Export method not found');
        return;
      }

      setDerivedImportMethod(method);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to get export document for import');
      setExportDocumentError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const FromExportImportComponent = FROM_EXPORT_IMPORT_COMPONENTS_BY_EXPORT_METHOD[derivedImportMethod as ExportMethod] || null;

  if (isLoading) {
    return <LoadingOverlay loadingText="Preparing to import data..." />;
  }

  if (FromExportImportComponent) {
    return <FromExportImportComponent exportDocument={exportDocument as DataIngestion} />;
  }

  return (
    <div>
      <h2>Choose an export document to import the data from</h2>
      <form onSubmit={handleSubmit}>
        <RelationshipInput
          path="exportDocument"
          label="Export document"
          description="Select the export document to import the data from"
          relationTo={['data-ingestion']}
          value={exportDocumentId ? { relationTo: 'data-ingestion', value: exportDocumentId } : null}
          hasMany={false}
          allowCreate={false}
          allowEdit={false}
          required={true}
          showError={exportDocumentError}
          filterOptions={{
            'data-ingestion': {
              targetCollectionSlug: {
                equals: collectionSlug,
              },
            },
          }}
          onChange={(value) => {
            setExportDocumentId((value?.value as string | null) ?? null);
            setExportDocumentError(false);
          }}
          Error={<FieldError path="exportDocument" message="Export document is required" showError={exportDocumentError} />}
        />
        <Button type="submit" buttonStyle="primary">
          Select
        </Button>
      </form>
    </div>
  );
};

export default ImportFromExportedRecord;
